/**
 * The document preview after a parse — which uploads get one, and what it shows.
 *
 * The structure and the fields are taken from the ProDoc review screen
 * (supplier, buyer, document, line items, totals, the file beside them); the
 * look is this app's. Pure — no React, no I/O — so the rules a reviewer
 * relies on are pinned by `tests/invoice-preview.test.ts`.
 *
 * **Every value comes from the service.** The header and lines are read from
 * the review rows' `effective` values, so a correction made here or on Review
 * shows the same everywhere; what a row does not carry (addresses, PO
 * reference, the document totals) is read from the producer's stored
 * `normalized` payload, as the engine sent it. Which fields may be corrected
 * is the review body's `correctable_fields` / `money_fields` / `rate_fields`,
 * never a list kept here.
 */

import type { DocumentRecord, ReviewRow } from './api'
import { invoiceTypeWords } from './invoiceType'

/** The producer name every document is stamped with before ProDoc reads it. */
const PRODOC = 'prodoc'
/** ProGST's own reader: a column mapping, or a platform report (`ingestion/profile.py`). */
const PROGST = 'progst-profile'

/**
 * Holds placed BEFORE the engine is called — the format gate, conversion,
 * a password, and a portal 2B in a books slot. Such a file carries the
 * producer's name (the pipeline stamps it up front) but ProDoc never saw it.
 */
const HELD_BEFORE_THE_ENGINE = new Set([
  'quarantine_unsupported_format',
  'quarantine_convert',
  'quarantine_password',
  'quarantine_2b_in_books_lane',
  'superseded',
])

/**
 * Whether this document was sent to ProDoc and so has a parse to preview.
 *
 * A split PDF's parent is not: its pages went to the engine as children, and
 * each child is previewed on its own.
 */
export function wentToProdoc(doc: DocumentRecord): boolean {
  if (doc.producer !== PRODOC) return false
  if (doc.status === 'split') return false
  if (doc.held && HELD_BEFORE_THE_ENGINE.has(doc.held)) return false
  return true
}

/**
 * The documents one upload run produced that ProDoc read — the uploads
 * themselves and, for a PDF split into several invoices, its children.
 * Order follows `docs`.
 */
export function previewQueue(
  docs: DocumentRecord[],
  uploadedIds: ReadonlySet<string>,
): DocumentRecord[] {
  return docs.filter(
    (d) =>
      (uploadedIds.has(d.id) || (d.parent_id !== null && uploadedIds.has(d.parent_id))) &&
      wentToProdoc(d),
  )
}

// -- values ------------------------------------------------------------------

type Normalized = Record<string, unknown> | null | undefined

function text(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** A key off the producer's raw header block, as the engine printed it. */
export function headerText(normalized: Normalized, key: string): string {
  return text(record(normalized?.header)?.[key])
}

/** `normalized.supplier.name` and friends — the shape both producers share. */
function party(normalized: Normalized, side: 'supplier' | 'buyer', key: 'name' | 'gstin'): unknown {
  return record(normalized?.[side])?.[key]
}

/**
 * One header value across a document's rows.
 *
 * A single invoice states its number once and every line carries it, so the
 * rows agree and that is the value. A register is many invoices: the rows
 * disagree, and the honest answer is "varies" — never the first row's value
 * shown as though it were the document's. Rows a person took out are skipped.
 * With no rows at all, the producer's own header is the fallback.
 */
export function headerValue(
  rows: ReviewRow[],
  field: string,
  fallback: unknown,
): { value: string; varies: boolean } {
  const seen = new Set<string>()
  for (const row of rows) {
    if (row.excluded_by) continue
    seen.add(text(row.effective[field]))
  }
  if (seen.size === 0) return { value: text(fallback), varies: false }
  if (seen.size > 1) return { value: '', varies: true }
  return { value: [...seen][0] ?? '', varies: false }
}

export interface FieldSpec {
  label: string
  /** The row field this is carried on — what a correction writes. */
  line?: string
  /** Read from the producer's header only; no row carries it. */
  header?: string
  fallback?: (normalized: Normalized) => unknown
  multiline?: boolean
  /** How a stored value reads (D-148: an invoice type's code in words). Shown, not saved. */
  shown?: (value: string) => string
}

export interface Section {
  title: string
  fields: FieldSpec[]
}

/** The header cards, in the order the ProDoc review screen shows them. */
export const SECTIONS: Section[] = [
  {
    title: 'Supplier',
    fields: [
      { label: 'Supplier name', line: 'supplier_name', fallback: (n) => party(n, 'supplier', 'name') },
      { label: 'Supplier GSTIN', line: 'gstin_supplier', fallback: (n) => party(n, 'supplier', 'gstin') },
      { label: 'Supplier state', header: 'supplier_state' },
      { label: 'Supplier address', header: 'supplier_address', multiline: true },
    ],
  },
  {
    title: 'Buyer',
    fields: [
      { label: 'Buyer name', line: 'buyer_name', fallback: (n) => party(n, 'buyer', 'name') },
      { label: 'Buyer GSTIN', line: 'gstin_buyer', fallback: (n) => party(n, 'buyer', 'gstin') },
      { label: 'Buyer state', header: 'buyer_state' },
      { label: 'Buyer address', header: 'buyer_address', multiline: true },
    ],
  },
  {
    title: 'Document',
    fields: [
      { label: 'Invoice no.', line: 'invoice_no', fallback: (n) => n?.invoice_no },
      { label: 'Invoice date', line: 'invoice_date', fallback: (n) => n?.invoice_date },
      { label: 'Place of supply', line: 'place_of_supply', fallback: (n) => n?.place_of_supply },
      { label: 'PO reference', header: 'po_ref' },
      // W3-16 (c), D-148: the invoice type, in words; the currency (D-143); and the value in that
      // currency — shown as the register states it, never as rupees and never corrected here.
      { label: 'Invoice type', line: 'invoice_type', shown: (v) => invoiceTypeWords(v) ?? v },
      { label: 'Currency', line: 'currency' },
      { label: 'Value in the invoice’s currency', line: 'fc_value' },
    ],
  },
]

/** A field's value for display: the rows when it is a row field, else the header. */
export function fieldValue(
  spec: FieldSpec,
  rows: ReviewRow[],
  normalized: Normalized,
): { value: string; varies: boolean } {
  if (spec.line) {
    const found = headerValue(rows, spec.line, spec.fallback?.(normalized))
    return spec.shown && found.value ? { ...found, value: spec.shown(found.value) } : found
  }
  return { value: spec.header ? headerText(normalized, spec.header) : '', varies: false }
}

/**
 * Whether a header box can be corrected here (D-66). A header value lives on every line, and the
 * correction goes to all of them at once through `corrections/every-row` (W3-13 stage 2), in one
 * transaction: every live line, or none.
 *
 * - the person may edit, and nothing else is saving;
 * - it is a row field the service corrects: the review body's list, never one kept here;
 * - **every line is on the page.** "The lines agree" is then a fact about the document, not about
 *   the first page of it; a box over lines nobody has seen would overwrite values that differ;
 * - **the lines agree.** A value that varies by line is corrected on Raw Data, line by line;
 * - at least one line is live: a document whose every line was taken out has nothing to correct.
 *
 * NOT gated on `accepted`: that flag is derived (no check objected), and gating on it locked every
 * field of a clean invoice, the one a misreading no check catches (a banner line read as the
 * supplier's name, 2026-10-05) and the one this screen exists to compare against the file.
 */
export function headerEditable(
  spec: FieldSpec,
  varies: boolean,
  state: {
    canEdit: boolean
    saving: boolean
    correctable: ReadonlySet<string>
    rows: ReviewRow[]
    totalRows: number
  },
): boolean {
  return (
    state.canEdit &&
    !state.saving &&
    !!spec.line &&
    state.correctable.has(spec.line) &&
    state.rows.length === state.totalRows &&
    !varies &&
    state.rows.some((row) => !row.excluded_by)
  )
}

// -- approval (W3-13 (3b), ADR-0035) -------------------------------------------------

/**
 * The remark an approval carries unless the person writes another (owner, 2026-10-07: optional,
 * pre-filled). The service requires a remark; this is what fills it when nobody types one.
 */
export const APPROVAL_REMARK = 'Fields checked against the file'

/**
 * Where a document stands with its approval:
 * - `approved` — a person has approved it (the verification record: who, when, the remark);
 * - `approvable` — read, with lines, and either not held or held only because ProDoc asked for a
 *   person to look (`review_extraction`). Approving answers exactly that hold, and the service
 *   releases it;
 * - `waits` — held for something approval does not answer (a month that is not the file's, a
 *   reading with no lines…), or no live line at all. The hold is resolved first; approving a
 *   document nothing can use would approve nothing.
 */
export type ApprovalState = 'approved' | 'approvable' | 'waits'

export function approvalState(doc: DocumentRecord, hasLiveLines: boolean): ApprovalState {
  if (doc.verified) return 'approved'
  if (doc.held && doc.held !== 'review_extraction') return 'waits'
  return hasLiveLines ? 'approvable' : 'waits'
}

/**
 * The next document in the list still to approve, after `from` and round to the start; null
 * when none is left. `approved` holds what was approved on this screen, which the list the
 * screen was opened with cannot know.
 */
export function nextToApprove(
  docs: DocumentRecord[],
  from: string,
  approved: ReadonlySet<string>,
): string | null {
  const at = docs.findIndex((d) => d.id === from)
  const order = [...docs.slice(at + 1), ...docs.slice(0, Math.max(at, 0))]
  const next = order.find((d) => d.id !== from && !d.verified && !approved.has(d.id))
  return next?.id ?? null
}

/**
 * A spreadsheet ProGST read itself — through a saved column mapping, or as a platform's settlement
 * report (W3-13 (3d)). Not one held for its columns (`needs_mapping`, which is stamped with the
 * same producer before anything is read), nor one refused before reading.
 */
export function readByProgst(doc: DocumentRecord): boolean {
  if (doc.producer !== PROGST) return false
  if (doc.held === 'needs_mapping') return false
  if (doc.held && HELD_BEFORE_THE_ENGINE.has(doc.held)) return false
  if (doc.held && PORTAL_LEDGER_HOLDS.has(doc.held)) return false
  return true
}

/**
 * The GST portal's two ledger downloads (W3-18 (8), D-97), and the holds a refused one carries.
 * Each holds a balance and no lines: the 3B reads it, and the preview has nothing to open.
 */
const PORTAL_LEDGERS = new Set(['credit_ledger', 'cash_ledger'])
const PORTAL_LEDGER_HOLDS = new Set([
  'quarantine_ledger_another_gstin',
  'quarantine_ledger_unreadable',
])

/**
 * What the preview opens: an invoice ProDoc read, or a spreadsheet ProGST read (3d). Never the
 * client's GST ledger (W3-17 (9), D-96): it is the month's check, holds no lines and awaits no
 * approval, and the working sheet is where it is read.
 */
export function previewable(doc: DocumentRecord): boolean {
  if (doc.doc_type === 'gst_ledger') return false
  if (doc.doc_type && PORTAL_LEDGERS.has(doc.doc_type)) return false
  return wentToProdoc(doc) || readByProgst(doc)
}

/** What Upload Data counts and opens: every document waiting, and those the preview can open. */
export function awaitingApproval(docs: DocumentRecord[]): {
  all: DocumentRecord[]
  previewable: DocumentRecord[]
} {
  const all = docs.filter((d) => d.awaiting_approval)
  return { all, previewable: all.filter(previewable) }
}

/** How a spreadsheet was read, from the document's own record; null when it does not say. */
export function readMapping(
  normalized: Record<string, unknown> | null | undefined,
):
  | {
      kind: 'profile'
      id: string
      name: string
      sheet: string
      headerRow: number
      /** D-91: where the layout was saved, when this file's headings were found elsewhere. */
      movedFrom?: { sheet: string; headerRow: number }
      /** D-92: read with no person, its guess proven against the sheet's own total. */
      proven?: true
    }
  | { kind: 'platform'; platform: string; sheet: string; headerRow: number }
  | null {
  const profile = normalized?.read_by_profile as
    | {
        id?: string
        name?: string
        sheet?: string
        header_row?: number
        moved_from?: { sheet?: string; header_row?: number }
      }
    | undefined
  if (profile?.id) {
    const was = profile.moved_from
    return {
      kind: 'profile',
      id: profile.id,
      name: profile.name ?? 'an unnamed layout',
      sheet: profile.sheet ?? '',
      headerRow: (profile.header_row ?? 0) + 1,
      ...(was ? { movedFrom: { sheet: was.sheet ?? '', headerRow: (was.header_row ?? 0) + 1 } } : {}),
      ...((normalized?.proven as { proven?: boolean } | undefined)?.proven ? { proven: true as const } : {}),
    }
  }
  const platform = normalized?.read_as_platform_report as
    | { platform?: string; sheet?: string; header_row?: number }
    | undefined
  if (platform?.platform) {
    return {
      kind: 'platform',
      platform: platform.platform,
      sheet: platform.sheet ?? '',
      headerRow: (platform.header_row ?? 0) + 1,
    }
  }
  return null
}

/**
 * A saved binding as the person who made it would say it. In ProDoc's words since X-14
 * (ADR-0045): a column by its heading (`header:Invoice No.`) or, with none, by its letter; a
 * figure read one column per rate (`rate:<head>`); several columns added together (a list).
 */
export function bindingWords(binding: unknown): string {
  if (Array.isArray(binding)) {
    return `${binding.map((b) => bindingWords(b)).join(', ')}, added together`
  }
  if (typeof binding !== 'string') return ''
  if (binding.startsWith('header:')) return `“${binding.slice(7)}”`
  if (/^[A-Z]{1,3}$/.test(binding)) return `column ${binding}, which has no heading`
  if (binding.startsWith('rate:')) return 'one column per GST rate'
  return binding
}

/** The heads a register's total row is checked on, in the order the return prints them. */
export const TOTAL_HEADS = ['taxable', 'igst', 'cgst', 'sgst', 'cess'] as const

/**
 * The verdict of the totals check, in words. No total row is said as unchecked — never as a pass
 * (ADR-0030 §4: `not_attested` is not an agreement).
 */
export function totalsVerdict(outcome: string | undefined): { tone: 'ok' | 'stop' | 'note'; words: string } {
  if (outcome === 'ok') {
    return { tone: 'ok', words: "The file's own total row agrees with the lines read, to the paisa." }
  }
  if (outcome === 'differs') {
    return { tone: 'stop', words: "The file's own total row does not agree with the lines read." }
  }
  return {
    tone: 'note',
    words: 'The file has no total row to check against, so the lines are as read and unchecked.',
  }
}

/**
 * Raw Data's queue (W3-13 (3c), D-75): what a reviewer can act on, less every document waiting for
 * approval. An invoice and — since 3d gave it its own view — a spreadsheet are approved in the
 * preview and reach Raw Data only once approved.
 */
export function rawDataQueue(all: DocumentRecord[]): {
  queue: DocumentRecord[]
  waiting: number
} {
  const reviewable = all.filter(
    (d) =>
      d.stage === 'mapping_review' ||
      d.stage === 'enrichment' ||
      d.held === 'review_extraction' ||
      // A trial balance's lines are worked out, so it is read straight to acceptance; once
      // approved its lines are corrected here like any other (W3-17 (8d)).
      (d.doc_type === 'trial_balance' && d.stage === 'acceptance'),
  )
  return {
    queue: reviewable.filter((d) => !d.awaiting_approval),
    waiting: reviewable.filter((d) => d.awaiting_approval).length,
  }
}

/** `1 document awaits approval`, `3 documents await approval`: the verb agrees with the count. */
export function documentsAwaitApproval(n: number): string {
  return `${n} document${n === 1 ? ' awaits' : 's await'} approval`
}

/**
 * Raw Data's line for the documents not on it yet (N34). Both verbs agree with the count: it read
 * "1 document awaits approval and come here once approved".
 */
export function rawDataWaitingWords(n: number): string {
  return `${documentsAwaitApproval(n)} and ${n === 1 ? 'comes' : 'come'} here once approved.`
}

/** What a return waits on, named: up to three files, then how many more. */
export function awaitingWords(docs: Array<{ source_filename: string | null }>): string {
  const names = docs.slice(0, 3).map((d) => d.source_filename ?? 'an unnamed file')
  const more = docs.length - names.length
  const listed = more > 0 ? `${names.join(', ')} and ${more} more` : names.join(', ')
  return `${documentsAwaitApproval(docs.length)}: ${listed}.`
}

// -- line items ----------------------------------------------------------------

export type ColumnKind = 'text' | 'money' | 'count' | 'percent'

export interface LineColumn {
  field: string
  label: string
  kind: ColumnKind
  /**
   * The least width a correctable text cell takes, as a class. Every cell took the same
   * 80px, which cut an item's name and an HSN code to their first few characters
   * (UAT, 2026-10-07); the table scrolls sideways rather than squeezing them.
   */
  minWidth?: string
}

/**
 * The line table. Money on a row is integer paise (the producer adapter
 * converts once, at intake); `qty` is a count and `gst_rate` a percent.
 */
export const LINE_COLUMNS: LineColumn[] = [
  { field: 'item', label: 'Item', kind: 'text', minWidth: 'min-w-56' },
  { field: 'hsn', label: 'HSN/SAC', kind: 'text', minWidth: 'min-w-28' },
  { field: 'qty', label: 'Qty', kind: 'count' },
  { field: 'uqc', label: 'Unit', kind: 'text', minWidth: 'min-w-20' },
  { field: 'rate', label: 'Rate', kind: 'money' },
  { field: 'disc_amount', label: 'Disc', kind: 'money' },
  { field: 'taxable', label: 'Taxable', kind: 'money' },
  { field: 'gst_rate', label: 'GST %', kind: 'percent' },
  { field: 'cgst', label: 'CGST', kind: 'money' },
  { field: 'sgst', label: 'SGST', kind: 'money' },
  { field: 'igst', label: 'IGST', kind: 'money' },
  { field: 'cess', label: 'Cess', kind: 'money' },
]

/** Paise → "1,23,456.78". Null stays a dash: not stated is not zero. */
export function rupees(paise: unknown): string {
  if (typeof paise !== 'number' || !Number.isFinite(paise)) return '—'
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function cellText(value: unknown, kind: ColumnKind): string {
  if (value === null || value === undefined || value === '') return '—'
  if (kind === 'money') return rupees(value)
  if (kind === 'percent') return `${value}%`
  return String(value)
}

/** What an edit box starts from: money in rupees, everything else as stored. */
export function editText(value: unknown, kind: ColumnKind): string {
  if (value === null || value === undefined) return ''
  if (kind === 'money' && typeof value === 'number') return String(value / 100)
  return String(value)
}

// -- totals ----------------------------------------------------------------------

/** `document_summary` keys, already integer paise on the wire (`units.py`). */
const TOTAL_LINES: Array<[string, string]> = [
  ['subtotal_paise', 'Subtotal'],
  ['global_discount_paise', 'Discount'],
  ['cgst_paise', 'CGST'],
  ['sgst_paise', 'SGST'],
  ['igst_paise', 'IGST'],
  ['cess_paise', 'Cess'],
  ['printed_round_off_paise', 'Round off'],
  ['grand_total_paise', 'Grand total'],
]

export interface TotalLine {
  label: string
  paise: number | null
}

/**
 * The document's printed totals. A key the producer did not send is left
 * out; a key it sent as null is listed with a dash — the two mean different
 * things and neither is zero. `null` when there is no summary at all.
 */
export function totals(normalized: Normalized): TotalLine[] | null {
  const summary = record(normalized?.document_summary)
  if (!summary) return null
  const out: TotalLine[] = []
  for (const [key, label] of TOTAL_LINES) {
    if (!(key in summary)) continue
    const v = summary[key]
    out.push({ label, paise: typeof v === 'number' ? v : null })
  }
  return out
}

export interface Charge {
  name: string
  paise: number | null
  /** False: the amount sits OUTSIDE the grand total. */
  included: boolean | null
}

export function charges(normalized: Normalized): Charge[] {
  const list = record(normalized?.document_summary)?.other_charges
  if (!Array.isArray(list)) return []
  return list.map((c) => {
    const r = record(c) ?? {}
    return {
      name: text(r.name) || 'Charge',
      paise: typeof r.amount_paise === 'number' ? r.amount_paise : null,
      included: typeof r.included === 'boolean' ? r.included : null,
    }
  })
}

/** The arithmetic check the producer ran; null when it did not say. */
export function checksumOk(doc: DocumentRecord, normalized: Normalized): boolean | null {
  if (typeof doc.checksum_ok === 'boolean') return doc.checksum_ok
  const v = record(normalized?.document_check)?.checksum_ok
  return typeof v === 'boolean' ? v : null
}

/**
 * How the browser's PDF viewer opens the file in the preview pane: the page
 * fitted to the pane's width, no thumbnail sidebar, and NO toolbar.
 *
 * The toolbar is what overflowed. Its width is the browser's, not ours — a
 * signed-in Chrome adds annotation and Drive buttons — and in a pane narrower
 * than it, the viewer scrolled sideways inside a pane that scrolls too: two
 * sets of scrollbars, the page number cut off on the left (2026-10-05). The
 * pane offers "Open in new tab" instead, where the full viewer has room.
 *
 * The pane now shows a local object URL (`blob:`) for bytes ProGST sent, not
 * a signed link to the store (2026-10-06), and the fragment still does its
 * job there: a `blob:` URL is looked up without its fragment (the File API
 * says so), and the PDF viewer reads its parameters from the fragment
 * whatever the URL's scheme. Nor can a fragment reach a server, so a signed
 * URL's signature would survive it too; a URL that already carries one keeps
 * it.
 */
export function pdfFrameUrl(url: string): string {
  return url.includes('#') ? url : `${url}#toolbar=0&navpanes=0&view=FitH`
}
